/**
 * Rate Limiting Hook
 * Provides rate limiting functionality for React components
 */

import { useState, useEffect, useCallback } from 'react';
import { 
  checkRateLimit, 
  recordAttempt, 
  getDelay, 
  getRateLimitStatus,
  RATE_LIMIT_CONFIG 
} from '../utils/rateLimiter';
import { logSecurityEvent, EVENT_TYPES } from '../utils/securityLogger';

export const useRateLimit = (type, identifier, config) => {
  const [isLimited, setIsLimited] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [attemptCount, setAttemptCount] = useState(0);
  const [maxAttempts, setMaxAttempts] = useState(config.MAX_ATTEMPTS);
  const [isLoading, setIsLoading] = useState(false);

  // Update status periodically
  useEffect(() => {
    const updateStatus = () => {
      const status = getRateLimitStatus(type, identifier, config);
      setIsLimited(status.isLocked);
      setRemainingTime(status.remainingTime || 0);
      setAttemptCount(status.attemptCount || 0);
      setMaxAttempts(status.maxAttempts || config.MAX_ATTEMPTS);
    };

    updateStatus();
    const interval = setInterval(updateStatus, 1000); // Update every second

    return () => clearInterval(interval);
  }, [type, identifier, config]);

  // Attempt to perform an action with rate limiting
  const attemptAction = useCallback(async (actionFunction, ...args) => {
    setIsLoading(true);
    
    try {
      // Check if rate limited
      const rateLimitCheck = await checkRateLimit(type, identifier, config);
      
      if (rateLimitCheck.isLimited) {
        setIsLimited(true);
        setRemainingTime(rateLimitCheck.remainingTime);
        
        // Log brute force attempt
        await logSecurityEvent(EVENT_TYPES.BRUTE_FORCE_ATTEMPT, null, {
          type: type,
          identifier: identifier,
          remainingTime: rateLimitCheck.remainingTime,
          reason: rateLimitCheck.reason
        });
        
        throw new Error(`Rate limited. Please try again in ${rateLimitCheck.remainingTime} seconds.`);
      }

      // Record attempt
      await recordAttempt(type, identifier, config);
      
      // Get delay if progressive delay is enabled
      const delay = getDelay(type, identifier, config);
      if (delay > 0) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      // Execute the action
      const result = await actionFunction(...args);
      
      // Clear rate limit on success
      if (result && result.success) {
        // Clear rate limit for successful actions
        const { clearRateLimit } = await import('../utils/rateLimiter');
        clearRateLimit(type, identifier);
      }
      
      return result;
      
    } catch (error) {
      console.error(`❌ Rate limited action failed:`, error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [type, identifier, config]);

  // Get formatted time remaining
  const getFormattedTimeRemaining = useCallback(() => {
    if (remainingTime <= 0) return null;
    
    const minutes = Math.floor(remainingTime / 60);
    const seconds = remainingTime % 60;
    
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }, [remainingTime]);

  // Get progress percentage
  const getProgressPercentage = useCallback(() => {
    return Math.min((attemptCount / maxAttempts) * 100, 100);
  }, [attemptCount, maxAttempts]);

  // Check if approaching rate limit
  const isApproachingLimit = useCallback(() => {
    return attemptCount >= maxAttempts * 0.8; // 80% of max attempts
  }, [attemptCount, maxAttempts]);

  return {
    isLimited,
    remainingTime,
    attemptCount,
    maxAttempts,
    isLoading,
    attemptAction,
    getFormattedTimeRemaining,
    getProgressPercentage,
    isApproachingLimit,
    remainingAttempts: maxAttempts - attemptCount
  };
};

// Specific hooks for common use cases
export const useLoginRateLimit = (email) => {
  return useRateLimit('login', email, RATE_LIMIT_CONFIG.LOGIN_ATTEMPTS);
};

export const usePasswordResetRateLimit = (email) => {
  return useRateLimit('password_reset', email, RATE_LIMIT_CONFIG.PASSWORD_RESET_ATTEMPTS);
};

export const useTOTPRateLimit = (userId) => {
  return useRateLimit('totp', userId, RATE_LIMIT_CONFIG.TOTP_ATTEMPTS);
};

export const useAPIRateLimit = (endpoint) => {
  return useRateLimit('api', endpoint, RATE_LIMIT_CONFIG.API_REQUESTS);
};
